import { z } from 'zod';

const documentObjectId = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');
const publicDocumentValues = z.record(z.string().trim().min(1).max(200), z.unknown())
    .refine((value) => Object.keys(value).length <= 300, 'Too many fields');

export const PublicDocumentShareIdSchema = z.string().regex(/^[a-f0-9]{64}$/);

export const PublicDocumentShareUpdateSchema = z.object({
    enabled: z.boolean(),
    ticketTemplateId: documentObjectId.nullable().optional(),
    regenerate: z.boolean().optional(),
}).strict();

export const PublicDocumentTicketSubmissionSchema = z.object({
    requesterName: z.string().trim().min(2).max(160),
    requesterEmail: z.string().trim().email().max(254),
    description: z.string().trim().max(5000).optional(),
    formData: publicDocumentValues.default({}),
    devexpressFormSnapshot: z.object({
        reportName: z.string().trim().min(1).max(240),
        fieldValuesByKey: publicDocumentValues,
        capturedAt: z.string().datetime().optional(),
        revision: z.number().int().min(1).optional(),
    }).strict(),
    website: z.string().max(200).optional(),
}).strict();

export const DynamicReportTemplateSeedSchema = z.object({
    sourceReportName: z.string().trim().min(1).max(240),
}).strict();

export const DocumentSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    title: z.string().min(1),
    code: z.string().optional(), // Document number
    version: z.string().default('1.0'),
    /** Mongoose paths (persisted) */
    documentType: z.string().optional(),
    classification: z.string().optional(),
    unit: z.string().optional(),
    assignmentTemplate: z.string().optional(),
    /** Legacy request aliases — merged server-side before save */
    typeId: z.string().optional(),
    classificationId: z.string().optional(),
    assignmentTemplateId: z.string().optional(),
    description: z.string().optional(),
    fileUrl: z.string().optional(),
    content: z.string().optional(), // For HTML documents
    tags: z.array(z.string()).optional(),
    relatedObligations: z.array(z.string()).optional(),
    relatedVendors: z.array(z.string()).optional(),
    /** Legacy request aliases — merged server-side before save */
    dynamicReportSource: z.boolean().optional(),
    reportBinding: z.object({
        reportKey: z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9-]*$/),
    }).strict().nullable().optional(),
    status: z.string().default('active'), // active, archived, obsolete
    approvalStatus: z.string().default('draft'), // draft, pending-approval, approved, rejected, expired
    reviewFrequency: z.enum(['none', 'monthly', 'quarterly', 'semi-annual', 'annual', 'biennial']).optional(),
    reviewDate: z.string().or(z.date()).optional(),
    nextReviewDate: z.string().or(z.date()).optional(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
});

export const CreateDocumentSchema = DocumentSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});

/**
 * PATCH semantics: omitted keys must not alter stored values.
 * `DocumentSchema.partial()` alone re-applies Zod `.default()` for missing keys
 * (e.g. forces approvalStatus → draft, version → 1.0 on every metadata save).
 */
export const UpdateDocumentSchema = DocumentSchema.partial()
    .omit({
        version: true,
        status: true,
        approvalStatus: true,
    })
    .extend({
        version: z.string().optional(),
        status: z.string().optional(),
        approvalStatus: z.string().optional(),
    });

export const documentFormSchema = z.object({
    documentName: z.string().min(1, "Document name is required"),
    order: z.string().min(1, "Order is required"),
    documentCode: z.string().optional(),
    documentCode2: z.string().optional(),
    takePlaceBetweenReports: z.union([z.boolean(), z.string()]).optional(),
    isDynamic: z.union([z.boolean(), z.string()]).optional(),
    isCreatedByReportingTool: z.union([z.boolean(), z.string()]).optional(),
    documentType: z.string().min(1, "Document type is required"),
    classificationDegree: z.string().min(1, "Classification degree is required"),
    relatedControlGroup: z.string().optional(),
    personalDataInventoryList: z.string().optional(),
    transferLocation: z.string().optional(),
    version: z.string().min(1, "Version is required"),
});
export const DocumentFormSchema = documentFormSchema;
